import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import { useConfirmDialog, ConfirmResponse } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { KeyboardKey } from "_shared/_utilities/keyboard-keys";
import DeleteIconButton from "_shared/_views/buttons/delete-icon-button";
import React from "react";

const MAX_URL_DISPLAY_LENGTH = 85;

const MainContainer = styled("div")(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));
const ListContainer = styled(List)(({ theme }) => ({
  paddingRight: theme.spacing(2.5),
  paddingTop: 0,
}));
const UrlInputField = styled(TextField)(() => ({
  position: "relative",
  width: "100%",
  ".MuiFormHelperText-root": {
    bottom: "-24px",
    position: "absolute",
  },
}));
const StyledListItemButton = styled(ListItemButton)(() => ({
  padding: 0
}));

type UrlListProps = {
  label: string;
  initialLinks: string[];
  onChange: (links: string[]) => void;
}

export default function UrlList(props: Readonly<UrlListProps>) {
  const { label, initialLinks, onChange } = props;

  const urlInputFieldRef = React.useRef<HTMLInputElement>(null);

  const initialLinksKey = initialLinks.join("\0");
  const [localLinks, setLocalLinks] = React.useState<{ sourceKey: string; links: string[] }>();
  const links = localLinks?.sourceKey === initialLinksKey ? localLinks.links : initialLinks;
  const [websiteUrl, setWebsiteUrl] = React.useState<string>("");
  const [error, setError] = React.useState<string>("\u00A0");
  const [urlValid, setUrlValid] = React.useState<boolean>(false);

  const confirm = useConfirmDialog();

  function handleLinkAdd() {
    try {
      if (!websiteUrl) return;
      if (links.includes(websiteUrl)) {
        throw new Error("URL already in list");
      }
      const updatedLinks = [...links, websiteUrl];
      setLocalLinks({ sourceKey: initialLinksKey, links: updatedLinks });
      onChange(updatedLinks);
    } catch (error) {
      if (error instanceof Error) {
        setError(error.message);
      }
    } finally {
      setUrlValid(false);
      setWebsiteUrl("");
      urlInputFieldRef.current?.blur();
    }
  }

  async function handleLinkRemove(link: string) {
    const response = await confirm({
      title: "Remove URL",
      message: `Are you sure you want to remove ${link} from the list?`,
      okButtonText: "Remove",
    });

    if (response === ConfirmResponse.Cancel) return;

    let updatedLinks = links.filter((url) => url !== link);
    setLocalLinks({ sourceKey: initialLinksKey, links: updatedLinks });
    onChange(updatedLinks);
  }

  function handleWebsiteUrlChange(
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) {
    try {
      const url = event.target.value.replace(/^https?:\/\//, "");
      setWebsiteUrl(url);
      setError("\u00A0");

      if (url.length < 1) return;
      setUrlValid(true);
    } catch (error) {
      if (error instanceof TypeError) {
        setError("Invalid URL");
        return;
      }
    }
  }

  function checkForSubmission(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === KeyboardKey.Enter && urlValid && websiteUrl.length > 0) {
      handleLinkAdd();
    }
  }

  return (
    <MainContainer>
      <ListContainer>
        {links?.length === 0 && (
          <StyledListItemButton disabled dense disableGutters>
            <ListItemText>No URLs</ListItemText>
          </StyledListItemButton>
        )}
        {links?.map((link: string) => (
          <ListItem
            key={link}
            dense={links.length > 3}
            disableGutters
            disablePadding
            secondaryAction={
              <Tooltip title="Remove URL From List">
                <span>
                  <DeleteIconButton
                    aria-label="delete"
                    onClick={() => handleLinkRemove(link)}
                  >
                    <DeleteIcon />
                  </DeleteIconButton>
                </span>
              </Tooltip>
            }
          >
            <ListItemText
              primary={
                <Link href={`https://${link}`} target="_blank">
                  {link.length > MAX_URL_DISPLAY_LENGTH
                    ? `${link.slice(0, MAX_URL_DISPLAY_LENGTH)}...`
                    : link}
                </Link>
              }
            />
          </ListItem>
        ))}
        <ListItem
          disableGutters
          secondaryAction={
            <Tooltip title="Add URL To List">
              <span>
                <IconButton
                  aria-label="add"
                  color="primary"
                  disabled={!urlValid || websiteUrl.length < 1}
                  onClick={handleLinkAdd}
                >
                  <AddIcon />
                </IconButton>
              </span>
            </Tooltip>
          }>
          <UrlInputField
            ref={urlInputFieldRef}
            label={label}
            variant="outlined"
            size="small"
            value={websiteUrl}
            onChange={handleWebsiteUrlChange}
            onBlur={handleLinkAdd}
            onKeyDown={checkForSubmission}
            error={error !== "\u00A0"}
            helperText={error}
          />
        </ListItem>
      </ListContainer>
    </MainContainer>
  );
}
